import Div from '@hellodarwin/core/lib/components/common/div';
import TagsList from '@hellodarwin/core/lib/components/common/hd-tag/tags-list';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import formatRange from '@hellodarwin/core/lib/components/projects/utils/format-range';
import dollarFormat from '@hellodarwin/core/lib/features/helpers/dollar-format';
import { getStringDate } from '@hellodarwin/core/lib/features/helpers/get-formatted-date';
import parse from '@hellodarwin/core/lib/features/helpers/parse';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Grants from '@hellodarwin/icons/dist/icons/Grants';
import Timeline from '@hellodarwin/icons/dist/icons/Timeline';
import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { ProjectSingleProps } from '..';
import { ProjectMetadataGrid, ProjectSingleHeaderContainer } from './styles';
interface ProjectSingleHeaderProps extends ProjectSingleProps {}
const ProjectSingleHeader: React.FC<ProjectSingleHeaderProps> = ({
  project,
  priority,
}) => {
  const theme = useTheme();
  const { t } = useTranslations();
  const { selectedLocale } = useLocale();

  const timelineText = (() => {
    if (!project.started_date || !project.end_date) {
      return t('project_single|project_continuous');
    }
    return formatRange({
      project,
      locale: selectedLocale,
      separator: selectedLocale === 'en' ? ' to ' : ' au ',
      formatValue: getStringDate,
      keys: ['started_date', 'end_date'],
    });
  })();

  return (
    <ProjectSingleHeaderContainer>
      <Div flex="column" gap={8}>
        {!!priority && (
          <Link to={`/game-plan/${priority?.project_id}`}>
            {priority?.title}
          </Link>
        )}
        <Typography.Title level={1} elementTheme="h5">
          {project.title}
        </Typography.Title>
      </Div>
      {!!project.description && (
        <Typography elementTheme="body1">
          {parse(project.description)}
        </Typography>
      )}
      <ProjectMetadataGrid>
        <Div flex="column" gap={4}>
          <Div
            flex="row"
            align="center"
            gap={8}
            style={{ color: theme.colors.grey_2 }}
          >
            <Grants size={14} />
            <Typography elementTheme="overline" nowrap>
              {t('project_single|project_estimated_budget')}
            </Typography>
          </Div>
          <Typography elementTheme="body2" color={theme.colors.purple_2} medium>
            {project.estimated_budget
              ? dollarFormat(project.estimated_budget, selectedLocale, true)
              : '-'}
          </Typography>
        </Div>
        <Div flex="column" gap={4}>
          <Div
            flex="row"
            align="center"
            gap={8}
            style={{ color: theme.colors.grey_2 }}
          >
            <Timeline size={14} />
            <Typography elementTheme="overline" nowrap>
              {t('project_single|project_timeline')}
            </Typography>
          </Div>
          <Typography elementTheme="body2">{timelineText}</Typography>
        </Div>
        <Div flex="column" gap={4}>
          <Typography
            elementTheme="overline"
            color={theme.colors.grey_2}
            nowrap
          >
            {t('project_single|project_type')}
          </Typography>
          {project.project_goals?.length ? (
            <TagsList
              tags={project.project_goals.map((text) => ({ text }))}
              mainColor={theme.colors.orange_1}
              limit={2}
              collapsedTagsInTooltip
            />
          ) : (
            <Typography elementTheme="body2">-</Typography>
          )}
        </Div>
        <Div flex="column" gap={4}>
          <Div
            flex="row"
            align="center"
            gap={8}
            style={{ color: theme.colors.grey_2 }}
          >
            <Timeline size={14} />
            <Typography elementTheme="overline" nowrap>
              {t('project_single|project_priority')}
            </Typography>
          </Div>
          <Typography elementTheme="body2">
            {project.priority ?? '-'}
          </Typography>
        </Div>
      </ProjectMetadataGrid>
    </ProjectSingleHeaderContainer>
  );
};
export default ProjectSingleHeader;
